// AGC: Analytics wrapper — sits between call sites and @capacitor-firebase/analytics
// AGC: Provides: PII scrubbing, consent gate, dev guard, offline queue, public API
import localforage from 'localforage'
import { FirebaseAnalytics } from '@capacitor-firebase/analytics'
import { PII_BLOCKLIST } from './analytics-events'
import type { AnalyticsEvent, EventName, EventParams, UserPropKey, CommonContext } from './analytics-events'
import { mirrorEventToFirestore } from '../services/analyticsEventWriter'

// AGC: Strip any param key present in the PII blocklist before forwarding to Firebase
export function scrubPii(params: Record<string, unknown> | undefined): Record<string, unknown> {
  if (!params) return {}
  const out: Record<string, unknown> = {}
  for (const [key, value] of Object.entries(params)) {
    if ((PII_BLOCKLIST as readonly string[]).includes(key)) {
      if (import.meta.env.DEV) {
        console.warn(`[analytics] dropped PII: ${key}`)
      }
      continue
    }
    out[key] = value
  }
  return out
}

// AGC: Key used to persist user consent preference across sessions
export const CONSENT_KEY = 'droidfarm_analytics_enabled'

// AGC: Returns true unless the user has explicitly opted out (stored as 'false')
export function isAnalyticsEnabled(): boolean {
  const stored = localStorage.getItem(CONSENT_KEY)
  if (stored === null) return true
  return stored !== 'false'
}

// AGC: Persists the user's analytics consent decision to localStorage
export function setAnalyticsConsent(enabled: boolean): void {
  localStorage.setItem(CONSENT_KEY, enabled ? 'true' : 'false')
}

// AGC: Returns true in dev mode unless VITE_ANALYTICS_DEV is set, suppressing events
export function shouldSuppressInDev(): boolean {
  return import.meta.env.DEV && !import.meta.env.VITE_ANALYTICS_DEV
}

// ---------------------------------------------------------------------------
// AGC: Offline event queue — stores events in localforage while the device is
// AGC: offline; flushed by flushQueue() on reconnect.
// AGC: Falls back to a module-level in-memory array when localforage is
// AGC: unavailable (e.g. test environments without IndexedDB / localStorage).
// ---------------------------------------------------------------------------

export const MAX_QUEUE_SIZE = 500
const QUEUE_KEY = 'droidfarm_analytics_queue'

// AGC: Isolated localforage instance so analytics queue never collides with other stores
const queueStore = localforage.createInstance({
  name: 'droidfarm',
  storeName: 'analytics_queue',
})

// AGC: In-memory fallback queue — used when localforage storage is not available
let _memQueue: AnalyticsEvent[] = []

async function _storeGet(): Promise<AnalyticsEvent[]> {
  try {
    const result = await queueStore.getItem<AnalyticsEvent[]>(QUEUE_KEY)
    // AGC: result may be null (first use) or undefined (storage unavailable) — both fall back to mem
    if (Array.isArray(result)) return result
    return _memQueue
  } catch {
    return _memQueue
  }
}

async function _storeSet(events: AnalyticsEvent[]): Promise<void> {
  _memQueue = events
  try {
    await queueStore.setItem(QUEUE_KEY, events)
  } catch {
    // AGC: Storage unavailable (e.g. jsdom test env) — in-memory fallback is already updated
  }
}

// AGC: Append one event to the tail; drops oldest entries when cap is exceeded
export async function enqueueEvent(event: AnalyticsEvent): Promise<void> {
  const current = await _storeGet()
  current.push(event)
  while (current.length > MAX_QUEUE_SIZE) {
    current.shift()
  }
  await _storeSet(current)
}

// AGC: Read the queue without removing items
export async function peekQueue(): Promise<AnalyticsEvent[]> {
  return _storeGet()
}

// AGC: Remove and return all queued events — call after a successful flush
export async function drainQueue(): Promise<AnalyticsEvent[]> {
  const current = await _storeGet()
  await _storeSet([])
  return current
}

// AGC: Empty the queue — used in tests and on consent revocation
export async function clearQueue(): Promise<void> {
  await _storeSet([])
}

// ---------------------------------------------------------------------------
// AGC: Public API — track, setUser, setUserProp, setScreen, flushQueue
// AGC: All functions respect the consent gate and dev suppression guard.
// AGC: Shared context object is auto-merged into every track() call.
// ---------------------------------------------------------------------------

// AGC: Module-level shared context — auto-merged into every track() call.
const _ctx: CommonContext = {
  ctx_screen: '/',
  ctx_release: import.meta.env.VITE_APP_VERSION ?? 'unknown',
}

// AGC: Override the release version string (call once at app boot from package.json)
export function setReleaseVersion(version: string): void {
  _ctx.ctx_release = version
}

// AGC: Start a named flow and return its UUID so callers can correlate events
export function setFlowContext(entryPoint: string): string {
  const flowId = crypto.randomUUID()
  _ctx.ctx_flow_id = flowId
  _ctx.ctx_entry_point = entryPoint
  return flowId
}

// AGC: Clear flow context after a wizard/flow completes or is abandoned
export function clearFlowContext(): void {
  _ctx.ctx_flow_id = undefined
  _ctx.ctx_entry_point = undefined
}

// AGC: Hash the farm ID (first 4 bytes of SHA-256) to avoid storing raw IDs
export async function setFarmContext(farmId: string | null): Promise<void> {
  if (!farmId) {
    _ctx.ctx_farm_id = undefined
    return
  }
  const encoder = new TextEncoder()
  const data = encoder.encode(farmId)
  const hashBuffer = await crypto.subtle.digest('SHA-256', data)
  const hashArray = Array.from(new Uint8Array(hashBuffer))
  _ctx.ctx_farm_id = hashArray.slice(0, 4).map(b => b.toString(16).padStart(2, '0')).join('')
}

// AGC: Build a snapshot of the shared context to embed in each event
function _snapshotContext(): Record<string, unknown> {
  const snap: Record<string, unknown> = {
    ctx_screen: _ctx.ctx_screen,
    ctx_release: _ctx.ctx_release,
  }
  if (_ctx.ctx_flow_id) snap.ctx_flow_id = _ctx.ctx_flow_id
  if (_ctx.ctx_entry_point) snap.ctx_entry_point = _ctx.ctx_entry_point
  if (_ctx.ctx_farm_id) snap.ctx_farm_id = _ctx.ctx_farm_id
  return snap
}

// AGC: Core tracking function — scrubs PII, injects context, enqueues when offline
// AGC: Also dual-writes to Firestore analytics_events for the in-app admin dashboard
export async function track<N extends EventName>(name: N, params: EventParams<N>): Promise<void> {
  if (!isAnalyticsEnabled()) return
  if (shouldSuppressInDev()) return
  const scrubbed = scrubPii(params as Record<string, unknown>)
  const merged = { ..._snapshotContext(), ...scrubbed }
  // AGC: Lazy-import auth to avoid triggering Firebase SDK init in test environments
  // AGC: that don't have a valid API key. The dynamic import resolves from the module cache
  // AGC: in production so there is no meaningful performance cost.
  const { auth } = await import('./firebase')
  const userId = auth.currentUser?.uid ?? null
  // AGC: Mirror to Firestore — fire-and-forget, never delays this function
  mirrorEventToFirestore(name, merged, userId)
  if (!navigator.onLine) {
    await enqueueEvent({ name, params: merged } as unknown as AnalyticsEvent)
    return
  }
  await FirebaseAnalytics.logEvent({ name, params: merged })
}

// AGC: Identify the current user — pass null to clear on sign-out
export async function setUser(userId: string | null): Promise<void> {
  if (!isAnalyticsEnabled()) return
  if (shouldSuppressInDev()) return
  await FirebaseAnalytics.setUserId({ userId })
}

// AGC: Set a single user property (e.g. language, build_context)
export async function setUserProp(key: UserPropKey, value: string | number): Promise<void> {
  if (!isAnalyticsEnabled()) return
  if (shouldSuppressInDev()) return
  await FirebaseAnalytics.setUserProperty({ key, value: String(value) })
}

// AGC: Update the current screen name — updates shared context AND notifies Firebase
export async function setScreen(screenName: string): Promise<void> {
  _ctx.ctx_screen = screenName
  if (!isAnalyticsEnabled()) return
  if (shouldSuppressInDev()) return
  await FirebaseAnalytics.setCurrentScreen({ screenName })
}

// AGC: Drain the offline queue and send all buffered events — call on navigator online event
export async function flushQueue(): Promise<void> {
  if (!isAnalyticsEnabled()) return
  if (shouldSuppressInDev()) return
  const events = await drainQueue()
  for (const evt of events) {
    await FirebaseAnalytics.logEvent({ name: evt.name, params: evt.params as Record<string, unknown> })
  }
}
